import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Grid,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { enqueueSnackbar } from "notistack";
import { ContaContabil } from "../../../../../../../types/api/geral/pesquisar-plano-contas";
import {
  formatarInputValorMonetário,
  formatarMonetárioParaNúmero,
  formatarValorMonetario,
} from "../../../../../../../helpers/masks";
import SelectPlanoContas from "../../../../../../../components/SelectsSimpleApiQuery/SelectPlanoContas";
import LabelPrinter from "../../../../../../../components/LabelPrinter";
import axiosClient from "../../../../../../../lib/axios";
import { useMutation, useQueryClient } from "react-query";
import { IDataCentroCustos } from "../../../../../../../types/api/contabilidade/pesquisar-centro-custos";
import SelectCentroCusto from "../../../../../../../components/SelectsSimpleApiQuery/SelectCentroCusto";

export default function DialogAddRateio({
  open,
  handleClose,
  codigo,
  valorTotal,
}: DialogAdicionarProps) {
  const queryClient = useQueryClient();

  const [valores, setValores] = useState<ValoresRateio[]>([]);
  const [numLinhas, setNumLinhas] = useState<string>("1");
  const [contaPadrao, setContaPadrao] = useState<ContaContabil | null>(null);

  //queries e mutations
  const { mutate: incluirRateio, isLoading } = useMutation({
    mutationKey: "incluir-rateio",
    mutationFn: () =>
      axiosClient.post("/financeiro/contas-pagar/incluir-rateio", {
        id_titulo: codigo,
        valor_total: valorTotal,
        rateios: valores.map((item) => ({
          conta_devedora_codigo: item.contaDevedora?.codigo_contabil,
          conta_devedora_custo: item.contaDevedora?.centro_custo?.id,
          valor: formatarMonetárioParaNúmero(item.valor),
        })),
      }),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao incluir rateio", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        "financeiro/contas-pagar/detalhar-titulo-pagar",
        codigo,
      ]);
      handleClose();
      enqueueSnackbar("Rateio incluído com sucesso", { variant: "success" });
    },
  });
  //

  //funcoes manipuladoras

  // Criar linhas automáticas com rateio
  const handleGerarRateio = () => {
    const numeroLinhas = Number(numLinhas);
    if (numeroLinhas <= 0) {
      enqueueSnackbar("Informe um número válido de linhas", {
        variant: "error",
      });
      return;
    }

    const valorTituloNum = valorTotal || 0;
    const valorBase = Math.floor((valorTituloNum / numeroLinhas) * 100) / 100;
    const linhas: ValoresRateio[] = [];

    for (let i = 0; i < numeroLinhas; i++) {
      linhas.push({
        contaDevedora: contaPadrao,
        valor: formatarValorMonetario(valorBase),
      });
    }

    //Arredondamento na ultima linha
    const totalRateado = valorBase * numeroLinhas;
    const diferenca = valorTituloNum - totalRateado;
    if (diferenca !== 0) {
      const ultimaLinha = linhas[linhas.length - 1];
      const valorUltimaLinha =
        formatarMonetárioParaNúmero(ultimaLinha.valor) + diferenca;
      ultimaLinha.valor = formatarValorMonetario(valorUltimaLinha);
    }

    setValores(linhas);
  };

  const handleChangeCentroCusto = (
    index: number,
    value: IDataCentroCustos | null
  ) => {
    const novasLinhas = [...valores];
    if (novasLinhas[index].contaDevedora) {
      novasLinhas[index].contaDevedora = {
        ...novasLinhas[index].contaDevedora!,
        centro_custo: value,
      };
    }
    setValores(novasLinhas);
  };

  const handleRemoveLinha = (index: number) => {
    const novasLinhas = valores.filter((_, idx) => idx !== index);
    setValores(novasLinhas);
  };

  const handleChangeContaDevedora = (
    index: number,
    value: ContaContabil | null
  ) => {
    const novasLinhas = [...valores];

    novasLinhas[index].contaDevedora = value;

    if (novasLinhas[index].contaDevedora) {
      novasLinhas[index].contaDevedora = {
        ...novasLinhas[index].contaDevedora!,
        centro_custo: null,
      };
    }

    setValores(novasLinhas);
  };

  const handleChangeValorRateio = (index: number, value: string) => {
    const novasLinhas = [...valores];
    novasLinhas[index].valor = value;
    setValores(novasLinhas);
  };

  const handleSubmit = () => {
    const temContaDevedoraNula = valores.some(
      (item) => item.contaDevedora === null
    );

    if (temContaDevedoraNula) {
      enqueueSnackbar("Todas as contas devedoras devem ser preenchidas", {
        variant: "error",
      });
      return;
    }

    const temValorInvalido = valores.some((item) => {
      const valor = formatarMonetárioParaNúmero(item.valor);
      return !valor || valor <= 0;
    });

    if (temValorInvalido) {
      enqueueSnackbar(
        "Todos os valores devem ser preenchidos e maiores que zero",
        { variant: "error" }
      );
      return;
    }

    incluirRateio();
  };
  //

  //valores importantes
  const totalRateio =
    valores.reduce(
      (acc, curr) => acc + (formatarMonetárioParaNúmero(curr.valor) || 0),
      0
    ) || 0;

  const valorTituloNumero = valorTotal || 0;
  const valorRestante = valorTituloNumero - totalRateio;
  //

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Rateio</DialogTitle>

      <DialogContent>
        <Grid mb={3} mt={0.7} container spacing={2} alignItems="center">
          <Grid size={{xs: 4}}>
            <TextField
              label="Nº de linhas"
              type="number"
              fullWidth
              value={numLinhas}
              onChange={(e) => setNumLinhas(e.target.value)}
            />
          </Grid>
          <Grid size={{xs: 7}}>
            <SelectPlanoContas
              inputLabel="Conta padrão (opcional)"
              value={contaPadrao}
              onChangeValue={(valor) => setContaPadrao(valor as ContaContabil)}
              extraParams={{ tipo: "Analítica" }}
              hasCentroCusto={false}
            />
          </Grid>
          <Grid size={{xs: 1}}>
            <Box height={"100%"}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleGerarRateio}
                sx={{ height: "100%" }}
              >
                Gerar
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Box mt={1}>
          {valores.map((linha, index) => (
            <Grid container spacing={2} key={index} alignItems="center" mb={2}>
              <Grid size={{xs: 4.3}}>
                <SelectPlanoContas
                  inputLabel="Conta devedora"
                  required
                  value={linha.contaDevedora}
                  onChangeValue={(valor) =>
                    handleChangeContaDevedora(index, valor as ContaContabil)
                  }
                  extraParams={{ tipo: "Analítica" }}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4.3}}>
                {linha.contaDevedora?.centro_custos ? (
                  <SelectCentroCusto
                    value={linha.contaDevedora?.centro_custo || null}
                    onChange={(value) => handleChangeCentroCusto(index, value)}
                    disabled={false}
                    extraParams={{
                      centro_custos: linha.contaDevedora?.centro_custos?.map(
                        (centro) => centro.id
                      ),
                    }}
                  />
                ) : (
                  <TextField
                    label="Centro de custo fiscal"
                    value="Não possui centro de custo"
                    variant="outlined"
                    fullWidth
                    disabled
                  />
                )}
              </Grid>

              <Grid size={{xs: 2.5}}>
                <TextField
                  fullWidth
                  label="Valor"
                  value={linha.valor}
                  onChange={(e) =>
                    handleChangeValorRateio(
                      index,
                      formatarInputValorMonetário(e.target.value)
                    )
                  }
                />
              </Grid>

              <Grid size={{xs: 0.5}}>
                {valores.length >= 1 && (
                  <Button
                    color="error"
                    variant="text"
                    onClick={() => handleRemoveLinha(index)}
                    sx={{ height: "56px" }}
                  >
                    <DeleteOutlineIcon />
                  </Button>
                )}
              </Grid>
            </Grid>
          ))}
        </Box>
      </DialogContent>

      <Stack
        p={3}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Stack direction={"row"} spacing={3}>
          <LabelPrinter
            size="small"
            valor={formatarValorMonetario(valorTotal)}
            titulo="Valor do título"
          />

          <LabelPrinter
            size="small"
            titulo="Total rateio"
            valor={formatarValorMonetario(totalRateio)}
            color={valorTotal === totalRateio ? "primary" : "error"}
          />

          <LabelPrinter
            size="small"
            titulo="Valor restante"
            valor={formatarValorMonetario(valorRestante)}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            autoFocus
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || valorTotal !== totalRateio}
          >
            {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            Salvar
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}

interface DialogAdicionarProps {
  open: boolean;
  handleClose: () => void;
  codigo: string;
  valorTotal: number;
}

export type ValoresRateio = {
  contaDevedora: ContaContabil | null;
  valor: string;
};
